// Parents: their students (consent first, for a student under 18), and one student in full.
// Students: their parents in the app and, from 18, whether to share with each.
import { useEffect, useState } from 'preact/hooks';
import { Link, navigate } from '../router.js';
import { config, rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Loading, Notice, Section, useRpc, useToast } from '../ui/components.jsx';
import { percent } from '../ui/question.jsx';
import { ProgressCards } from './progress.jsx';
import { PaymentRow } from './fees.jsx';
import { ReminderPrompt } from '../ui/reminder-prompt.jsx';
import { NOTICE_VERSION, NoticeBody } from './privacy.jsx';
import { dateLong, dateShort, dateTile, dayLabel, firstName, greeting, initialOf, istDate, timeLabel, timeRange } from '../format.js';

export const RELATION_LABEL = { mother: 'Mother', father: 'Father', guardian: 'Guardian', parent: 'Parent' };
const waLink = (text) => `https://wa.me/${config.whatsapp}?text=${encodeURIComponent(text)}`;
const first = (name) => firstName({ full_name: name }) || 'your child';

// "today", "tomorrow" or "8 Oct"
function dayWord(ts) {
  const iso = istDate(ts);
  const d = dayLabel(iso);
  return d.prefix ? d.prefix.toLowerCase() : dateShort(iso).replace(/ \d{4}$/, '');
}
const started = (n) => Date.parse(n.starts_at) <= Date.now();
const nextLabel = (n) => (started(n) ? 'On now' : `${dayWord(n.starts_at).replace(/^./, (c) => c.toUpperCase())}, ${timeLabel(n.starts_at)}`);

function consentLine(c, who) {
  if (!c) return '';
  if (c.method === 'recorded') return `Recorded by the academy on ${dateShort(istDate(c.given_at))}${c.note ? `: ${c.note}` : ''}`;
  return `${who} on ${dateLong(istDate(c.given_at))}`;
}

// Across a student's batches: attendance, the next class, the latest mark on the register.
function summary(c) {
  const bs = c.batches || [];
  const marked = bs.reduce((t, b) => t + Number(b.attendance.marked || 0), 0);
  const present = bs.reduce((t, b) => t + Number(b.attendance.online || 0) + Number(b.attendance.offline || 0), 0);
  const next = bs.flatMap((b) => b.next_classes.map((n) => ({ ...n, batch: b.batch })))
    .sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at)))[0] || null;
  const latest = bs.flatMap((b) => b.recent_attendance).sort((a, b) => String(b.starts_at).localeCompare(String(a.starts_at)))[0] || null;
  const mocks = bs.flatMap((b) => b.mocks);
  const mockAvg = mocks.length ? Math.round(mocks.reduce((t, m) => t + percent(m.score, m.max_score), 0) / mocks.length) : null;
  return { marked, present, rate: marked ? Math.round((present * 100) / marked) : null, next, latest, mockAvg };
}

/* ---------------------------------------------------------------- consent, for a student under 18 */
function ConsentCard({ c, onDone }) {
  const toast = useToast();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const name = first(c.name);
  const give = async (e) => {
    e.preventDefault();
    if (!agree) { setErr('Tick the box first.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await rpc('parent_consent', { p_student_id: c.id, p_adult: true, p_notice_version: NOTICE_VERSION });
      toast(`Thank you. You can now follow ${name}’s classes and progress.`);
      onDone(res);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  return (
    <form class="consent-card" onSubmit={give} aria-labelledby={`cc-${c.id}`} noValidate>
      <p class="eyebrow">Your consent is needed</p>
      <h2 id={`cc-${c.id}`}>{c.name} is under 18</h2>
      <p>The academy asks a parent or guardian before it shares a student’s progress. Once you agree:</p>
      <ul class="consent-points">
        <li><Icon name="usercheck" /><span>You see {name}’s attendance, how many class recordings they have opened, marks in tests and mock exams, topics to work on, coming classes and fee receipts.</span></li>
        <li><Icon name="bell" /><span>You can get a notification when {name} is marked absent or misses a compulsory test.</span></li>
        <li><Icon name="lock" /><span>The academy uses {name}’s details as the privacy notice below describes. You can withdraw your consent at any time.</span></li>
      </ul>
      <details class="notice-details">
        <summary>Read the privacy notice</summary>
        <NoticeBody headingLevel={3} />
      </details>
      <label class="check consent-check">
        <input type="checkbox" checked={agree} onChange={(e) => { setAgree(e.currentTarget.checked); setErr(null); }} />
        <span>I am {c.name}’s parent or guardian, I am 18 or older, and I agree to the academy using {name}’s details as the notice says.</span>
      </label>
      {err ? <p class="field-error" role="alert">{err}</p> : null}
      <div class="form-actions"><Button type="submit" variant="gold" icon="check" busy={busy}>Give consent</Button></div>
      <p class="fine"><Icon name="info" /><span>Not {name}’s parent or guardian? Please don’t continue, and <a href={waLink(`Hi Vidyavedh Academy, the learning app shows me ${c.name}, but I am not their parent or guardian.`)} target="_blank" rel="noopener">tell the academy</a>.</span></p>
    </form>
  );
}

function WaitingCard({ c }) {
  const name = first(c.name);
  return (
    <div class="consent-card is-waiting">
      <p class="eyebrow">Waiting for {name}</p>
      <h2>{c.name}</h2>
      <p>{name} is 18 or over, so {name} decides whether to share their attendance, marks and classes with you. They can turn it on in the app, in Profile, Sharing with parents.</p>
    </div>
  );
}

function ChildCard({ c }) {
  const s = summary(c);
  const absent = s.latest && s.latest.status === 'absent';
  return (
    <Link class="child-card" href={`/children/${c.id}`}>
      <span class="child-top">
        <span class="avatar avatar-sm" aria-hidden="true">{initialOf(c.name)}</span>
        <span class="child-name">
          <strong>{c.name}</strong>
          <small>{c.batches.map((b) => b.batch.name).join(' · ') || 'No batch at the moment'}</small>
        </span>
        <Icon name="next" />
      </span>
      {absent ? (
        <span class="child-alert"><Icon name="alert" />Absent from {s.latest.title || 'class'} {/^\d/.test(dayWord(s.latest.starts_at)) ? 'on ' : ''}{dayWord(s.latest.starts_at)}</span>
      ) : null}
      <span class="child-facts">
        <span><small>Attendance</small><strong>{s.rate === null ? '–' : `${s.rate}%`}</strong></span>
        <span><small>Next class</small><strong>{s.next ? nextLabel(s.next) : 'None this week'}</strong></span>
        <span><small>Mock exams</small><strong>{s.mockAvg === null ? '–' : `${s.mockAvg}%`}</strong></span>
      </span>
    </Link>
  );
}

/* ---------------------------------------------------------------- a parent's home */
export function ParentHome() {
  const { me, reloadHome } = useApp();
  const { data, error, loading, reload, setData } = useRpc('parent_home', {});
  useEffect(() => { reloadHome(true); }, []);
  const kids = (data && data.children) || [];
  const asking = kids.filter((c) => c.state === 'needs_parent');
  const active = kids.filter((c) => c.state === 'active');
  const waiting = kids.filter((c) => c.state === 'needs_student');
  return (
    <Shell>
      <div class="hello">
        <p class="hello-date">{dateLong(new Date().toISOString())}</p>
        <h1 class="page-title">{greeting()}, {firstName(me)}</h1>
      </div>
      {loading && !data ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !kids.length ? (
        <Empty icon="family" title="No students linked yet"
          action={<Button variant="line" icon="chat" href={waLink(`Hi Vidyavedh Academy, I signed in to the learning app as a parent (${me.email}) but can't see my child.`)} target="_blank" rel="noopener">Message the academy</Button>}>
          The academy links parents to their child with the email from the admission form. You signed in as {me.email}.
        </Empty>
      ) : null}
      {asking.map((c) => <ConsentCard c={c} key={c.id} onDone={(res) => setData(res)} />)}
      {active.length ? (
        <Section title={active.length === 1 ? 'Your child' : 'Your children'} id="ph-kids">
          <div class="child-grid">{active.map((c) => <ChildCard c={c} key={c.id} />)}</div>
        </Section>
      ) : null}
      {active.length ? <ReminderPrompt title="Know when they miss a class or a test" label="Alerts" done="Alerts are on for this device"
        text={`A notification on this phone when ${active.length === 1 ? first(active[0].name) : 'your child'} is marked absent or misses a compulsory test.`} /> : null}
      {waiting.map((c) => <WaitingCard c={c} key={c.id} />)}
      {kids.length ? <p class="fine"><Icon name="notice" /><span>How the app uses your details and your child’s: <Link href="/privacy">privacy notice</Link>.</span></p> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- one student, for a parent */
function NextClasses({ list }) {
  if (!list.length) return <p class="muted">No classes in the next 7 days.</p>;
  return (
    <ul class="list">
      {list.map((n) => {
        const t = dateTile(istDate(n.starts_at));
        return (
          <li class="list-row" key={n.id}>
            <span class="date-tile"><strong>{t.day}</strong><small>{t.month}</small></span>
            <span class="list-text"><strong>{n.title || 'Class'}</strong><small>{timeRange(n.starts_at, n.ends_at)}</small></span>
            {started(n) ? <Chip tone="ok">On now</Chip> : null}
          </li>
        );
      })}
    </ul>
  );
}

export function ChildPage({ id }) {
  const toast = useToast();
  const { reloadHome } = useApp();
  const { data, error, loading, reload } = useRpc('parent_child', { p_student_id: id }, [id]);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = data && data.student;
  const name = s ? first(s.name) : '';
  const withdraw = async () => {
    setBusy(true);
    try {
      await rpc('parent_withdraw', { p_student_id: id });
      toast('Consent withdrawn. The academy has been told.');
      reloadHome(true);
      navigate('/');
    } catch (ex) { toast(ex.message, 'bad'); setBusy(false); }
  };
  return (
    <Shell backTo="/" backLabel="Home">
      {loading && !data ? <Loading rows={5} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {s ? (
        <>
          <p class="eyebrow">{s.minor ? 'Student under 18' : 'Student'}</p>
          <h1 class="page-title">{s.name}</h1>
          {!data.batches.length ? <Empty icon="layers" title="No batch at the moment">{name}’s batches show here while their access is on.</Empty> : null}
          {data.batches.map((b) => (
            <section class="progress-batch" key={b.batch.id} aria-labelledby={`cb-${b.batch.id}`}>
              <h2 id={`cb-${b.batch.id}`}><span class="subject subject-xs">{b.batch.subject || '•'}</span>{b.batch.name}</h2>
              {b.access_until ? <p class="muted">Access until {dateShort(b.access_until)}</p> : null}
              <Section title="Coming classes" id={`cb-${b.batch.id}-next`}><NextClasses list={b.next_classes} /></Section>
              <ProgressCards p={b} staff idBase={`cb-${b.batch.id}`} />
            </section>
          ))}
          <Section title="Fees and receipts" id="cp-fees">
            {data.payments.length
              ? <ul class="list pay-list">{data.payments.map((x) => <PaymentRow key={x.id} x={x} />)}</ul>
              : <Empty icon="receipt" title="No payments yet">Payments for {name} and their receipts show here.</Empty>}
          </Section>
          <Section title={s.minor ? 'Your consent' : 'Sharing'} id="cp-consent">
            <div class="card">
              {s.minor ? (
                <>
                  {data.consent ? <p>{consentLine(data.consent, 'You gave consent in the app')}.</p> : null}
                  {asking ? (
                    <Notice tone="warn" icon="alert"><div>
                      <span>Withdraw your consent? You will stop seeing {name}’s details, and the academy will be told.</span>
                      <span class="row">
                        <Button variant="danger" size="sm" busy={busy} onClick={withdraw}>Withdraw consent</Button>
                        <Button variant="ghost" size="sm" onClick={() => setAsking(false)}>Keep it</Button>
                      </span>
                    </div></Notice>
                  ) : <div class="form-actions"><Button variant="line" size="sm" icon="userx" onClick={() => setAsking(true)}>Withdraw consent</Button></div>}
                </>
              ) : (
                <p>{name} chose to share their progress with you{data.consent ? ` on ${dateLong(istDate(data.consent.given_at))}` : ''}. {name} can stop sharing at any time.</p>
              )}
              <p class="fine"><Icon name="notice" /><span>See the <Link href="/privacy">privacy notice</Link>.</span></p>
            </div>
          </Section>
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- a student's sharing */
export function Sharing() {
  const toast = useToast();
  const { reloadHome } = useApp();
  const { data, error, loading, reload, setData } = useRpc('my_sharing', {});
  const [busy, setBusy] = useState('');
  const share = async (x, on) => {
    setBusy(x.id + on);
    try {
      const res = await rpc('student_share', { p_parent_id: x.id, p_share: on, p_notice_version: on ? NOTICE_VERSION : null });
      setData(res);
      reloadHome(true);
      toast(on ? `${x.name} can now see your progress` : `${x.name} won’t see your progress`);
    } catch (ex) { toast(ex.message, 'bad'); }
    setBusy('');
  };
  return (
    <Shell title="Sharing with parents" backTo="/profile" backLabel="Profile">
      {loading && !data ? <Loading rows={2} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <p class="muted">
            {data.minor
              ? 'You’re under 18, so the academy asks your parent or guardian for consent. Once they give it, they see your attendance, recordings opened, marks, coming classes and fee receipts, and can get a notification when you’re marked absent or miss a compulsory test.'
              : 'You’re 18 or over, so you decide whether each parent sees your attendance, recordings opened, marks, coming classes and fee receipts (and gets a notification when you’re marked absent or miss a compulsory test). You can change this at any time.'}
          </p>
          {!data.parents.length ? <Empty icon="family" title="No parents in the app">The academy adds parents with the email from your admission form.</Empty> : null}
          <ul class="list share-list">
            {data.parents.map((x) => (
              <li class="list-row share-row" key={x.id}>
                <span class="list-text">
                  <strong>{x.name}</strong>
                  <small>{RELATION_LABEL[x.relation] || 'Parent'} · {x.email}{x.signed_in ? '' : ' · hasn’t signed in yet'}</small>
                </span>
                {data.minor ? (
                  <Chip tone={x.state === 'active' ? 'ok' : 'plain'}>{x.state === 'active' && x.consent ? `Consent given ${dateShort(istDate(x.consent.given_at))}` : x.state === 'active' ? 'Consent given' : 'No consent yet'}</Chip>
                ) : x.state === 'active' ? (
                  <span class="share-actions">
                    <Chip tone="ok">Sharing</Chip>
                    <Button variant="line" size="sm" busy={busy === x.id + false} onClick={() => share(x, false)} aria-label={`Stop sharing with ${x.name}`}>Stop sharing</Button>
                  </span>
                ) : (
                  <span class="share-actions">
                    <Button variant="gold" size="sm" icon="check" busy={busy === x.id + true} onClick={() => share(x, true)} aria-label={`Share with ${x.name}`}>Share</Button>
                    {!x.declined ? <Button variant="ghost" size="sm" busy={busy === x.id + false} onClick={() => share(x, false)} aria-label={`Don’t share with ${x.name}`}>Don’t share</Button> : <Chip>Not sharing</Chip>}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <details class="notice-details card">
            <summary>Read the privacy notice</summary>
            <NoticeBody headingLevel={3} />
          </details>
        </>
      ) : null}
    </Shell>
  );
}

// On a student's Home: parents in the app are waiting for them to decide.
export function SharingRequest({ n }) {
  if (!n) return null;
  return (
    <Link href="/sharing" class="news news-share">
      <span class="news-icon"><Icon name="family" /></span>
      <span class="news-text"><strong>{n === 1 ? 'Share your progress with your parent?' : 'Share your progress with your parents?'}</strong><span>You’re 18 or over, so you decide</span></span>
      <Icon name="next" />
    </Link>
  );
}
